import { inject } from '@angular/core';
import { TicketSendingOptionsActions } from '@core/data-access/store/shared/actions/ticket-sending-option.actions';
import { TicketHolderFormActions } from '@core/features/ticket-assignment/store/ticket-assignment.actions';
import { OrderSummaryActions } from '@features/order-summary/data-access/store/order-summary.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { SendingOptionModel } from '@products/models/product-selection.model';
import { BookingActions } from '@store/products/booking/booking.actions';
import { selectBookedTariffUuids } from '@store/products/booking/booking.selectors';
import { SendingOptionActions } from '@store/products/product-selection/product-selection.actions';
import { getSelectedSendingOption } from '@store/products/product-selection/product-selection.selectors';
import { find, isEmpty } from 'lodash';
import { filter, map } from 'rxjs';
import { selectSelectedTicketSendingOptions } from '../shared.selectors';

export const addSelectedTicketSendingOption$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(BookingActions.setBookedTariffUuids),
      concatLatestFrom(() => store.select(getSelectedSendingOption)),
      map(([{ addBookedTariffUuids, removeBookedTariffUuids }, selectedTicketSendingOption]) =>
        TicketSendingOptionsActions.setTicketSendingOptions({
          selectedTicketSendingOption,
          addBookedTariffUuids,
          removeBookedTariffUuids
        })
      )
    ),
  { functional: true }
);

export const removeSelectedTicketSendingOption$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(BookingActions.removeBookedTariffUuids),
      concatLatestFrom(() => store.select(selectSelectedTicketSendingOptions)),
      filter(([_, selectedTicketSendingOptions]) => !isEmpty(selectedTicketSendingOptions)),
      map(([{ bookedTariffUuids }]) => TicketSendingOptionsActions.removeTicketSendingOptions(bookedTariffUuids))
    ),
  { functional: true }
);

export const setSelectedSendingOptionsOnSendingOptionChange$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(SendingOptionActions.setSendingOptions),
      map(({ sendingOptions }) => find(sendingOptions, ({ isSelected }: SendingOptionModel) => isSelected)?.value),
      concatLatestFrom(() => [store.select(selectBookedTariffUuids)]),
      map(([selectedTicketSendingOption, bookedTariffUuids]) => ({
        selectedTicketSendingOption,
        tariffUuids: bookedTariffUuids
      })),
      map(({ selectedTicketSendingOption, tariffUuids }) =>
        TicketSendingOptionsActions.setTicketSendingOptions({
          selectedTicketSendingOption,
          addBookedTariffUuids: tariffUuids,
          removeBookedTariffUuids: []
        })
      )
    ),
  { functional: true }
);

export const setSelectedSendingOptionOnChangedTicketSendingOptionOrSendToOwner$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(
        TicketHolderFormActions.setSendToOwnerFormField,
        OrderSummaryActions.setTicketHolderSendingOptionFormFields
      ),
      concatLatestFrom(() => store.select(selectSelectedTicketSendingOptions)),
      filter(([_, selectedTicketSendingOptions]) => !isEmpty(selectedTicketSendingOptions)),
      map(([{ tariffUuid, selectedTicketSendingOption }]) =>
        TicketSendingOptionsActions.setTicketSendingOptions({
          selectedTicketSendingOption,
          addBookedTariffUuids: [tariffUuid],
          removeBookedTariffUuids: []
        })
      )
    ),
  { functional: true }
);
